import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Marquee from "../components/Marquee";
import ProductCard from "../components/ProductCard";
import { ArrowRight } from "lucide-react";
import { api, absUrl } from "../lib/api";
import { useSettings } from "../context/SettingsContext";

function Skeleton() {
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-8">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="animate-pulse">
          <div className="aspect-[3/4] bg-neutral-100" />
          <div className="h-3 bg-neutral-100 mt-3 w-3/4 mx-auto" />
          <div className="h-3 bg-neutral-100 mt-2 w-1/3 mx-auto" />
        </div>
      ))}
    </div>
  );
}

export default function Home() {
  const { settings } = useSettings();
  const [newDrop, setNewDrop] = useState(null);
  const [bestSeller, setBestSeller] = useState(null);
  const videoRef = React.useRef(null);

  useEffect(() => {
    const load = () => {
      api.getProducts({ tag: "new-drop" }).then((d) => setNewDrop(d.slice(0, 8)));
      api.getProducts({ tag: "best-seller" }).then((d) => setBestSeller(d.slice(0, 4)));
    };
    load();
    window.addEventListener("focus", load);
    return () => window.removeEventListener("focus", load);
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = true;
    video.setAttribute('muted', '');
    video.setAttribute('webkit-playsinline', '');
    const tryPlay = () => video.play().catch(() => {});
    tryPlay();
    video.addEventListener('loadeddata', tryPlay);
    video.addEventListener('canplay', tryPlay);
    const onInteract = () => tryPlay();
    document.addEventListener('touchstart', onInteract, { passive: true });
    document.addEventListener('click', onInteract);
    const onVisible = () => { if (!document.hidden) tryPlay(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      video.removeEventListener('loadeddata', tryPlay);
      video.removeEventListener('canplay', tryPlay);
      document.removeEventListener('touchstart', onInteract);
      document.removeEventListener('click', onInteract);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [settings.heroVideo]);

  const hero = newDrop && newDrop[0];
  const heroAlt = (bestSeller && bestSeller[0]) || (newDrop && newDrop[1]);
  const editorial = newDrop && newDrop[0];
  const igProducts = newDrop || [];

  return (
    <div className="fade-up">
      <Marquee
        items={["LIMITED STOCK", settings.tagline, settings.shippingText, "NEW DROP"]}
      />

      {/* HERO */}
      <section className="relative h-[88vh] md:h-screen overflow-hidden bg-black">
        {settings.heroVideo ? (
          <video
            ref={videoRef}
            key={settings.heroVideo}
            className="absolute inset-0 h-full w-full object-cover object-[50%_30%]"
            src={absUrl(settings.heroVideo)}
            poster={settings.heroPoster ? absUrl(settings.heroPoster) : undefined}
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
          />
        ) : (
          hero && <img src={absUrl(hero.images[0])} alt={hero.name} className="absolute inset-0 h-full w-full object-cover" />
        )}
        <div className="absolute inset-0 bg-black/20 pointer-events-none" />
        <div className="absolute inset-x-0 bottom-0 flex flex-col items-center text-center px-6 pb-12 md:pb-16 pointer-events-none">
          <p className="text-white text-xs md:text-sm tracking-[0.4em] mb-3 drop-shadow">{settings.tagline}</p>
          <h1 className="font-display text-white text-4xl md:text-6xl font-medium tracking-wide drop-shadow-lg">
            NEW SEASON
          </h1>
          <Link
            to="/shop"
            className="mt-6 inline-flex items-center gap-2 bg-white text-black px-9 py-3.5 text-xs tracking-[0.25em] hover:bg-neutral-200 transition-colors pointer-events-auto"
          >
            SHOP NOW <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      {/* NEW DROP */}
      <section className="mx-auto max-w-[1400px] px-4 md:px-8 py-16">
        <div className="flex items-end justify-between mb-8">
          <div>
            <p className="text-xs tracking-[0.3em] text-neutral-400">JUST ARRIVED</p>
            <h2 className="font-display text-3xl md:text-4xl tracking-wide mt-2">NEW DROP</h2>
          </div>
          <Link to="/shop?tag=new-drop" className="text-xs tracking-[0.2em] border-b border-black pb-1 hover:opacity-60">
            VIEW ALL
          </Link>
        </div>
        {!newDrop ? <Skeleton /> : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-8">
            {newDrop.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        )}
      </section>

      {/* Editorial banner */}
      <section className="relative h-[50vh] md:h-[70vh] overflow-hidden bg-neutral-100">
        {(settings.editorialImage || editorial) && (
          <img
            src={settings.editorialImage ? absUrl(settings.editorialImage) : absUrl(editorial.images[0])}
            alt="editorial"
            className="h-full w-full object-cover object-top"
          />
        )}
        <div className="absolute inset-0 bg-black/30" />
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
          <h2 className="font-display text-white text-3xl md:text-5xl tracking-wide drop-shadow-lg">
            {settings.editorialText || "DESIGNED TO BE LOVED"}
          </h2>
          <Link
            to="/shop"
            className="mt-6 inline-flex items-center gap-2 bg-white text-black px-8 py-3 text-xs tracking-[0.25em] hover:bg-neutral-200 transition-colors"
          >
            EXPLORE THE COLLECTION
          </Link>
        </div>
      </section>

      {/* BEST SELLER */}
      <section className="mx-auto max-w-[1400px] px-4 md:px-8 py-16">
        <div className="flex items-end justify-between mb-8">
          <div>
            <p className="text-xs tracking-[0.3em] text-neutral-400">LOVED BY ALL</p>
            <h2 className="font-display text-3xl md:text-4xl tracking-wide mt-2">BEST SELLER</h2>
          </div>
          <Link to="/shop?tag=best-seller" className="text-xs tracking-[0.2em] border-b border-black pb-1 hover:opacity-60">
            VIEW ALL
          </Link>
        </div>
        {!bestSeller ? <Skeleton /> : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-8">
            {bestSeller.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        )}
      </section>

      {/* Instagram */}
      <section className="mx-auto max-w-[1400px] px-4 md:px-8 pb-16 text-center">
        <p className="text-xs tracking-[0.3em] text-neutral-400">@{settings.brandName.toLowerCase()}</p>
        <h2 className="font-display text-3xl md:text-4xl tracking-wide mt-2 mb-8">VISIT OUR INSTAGRAM</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {(settings.instagramImages && settings.instagramImages.length > 0
            ? settings.instagramImages.slice(0, 8).map((img, i) => ({ id: `ig-${i}`, src: img }))
            : igProducts.slice(0, 4).map((p) => ({ id: p.id, src: p.images[0] }))
          ).map((item) => (
            <a key={item.id} href={settings.instagram} target="_blank" rel="noreferrer" className="group relative overflow-hidden aspect-square bg-neutral-100">
              <img src={absUrl(item.src)} alt="instagram" className="h-full w-full object-cover img-zoom" />
            </a>
          ))}
        </div>
        <a href={settings.instagram} target="_blank" rel="noreferrer" className="mt-8 inline-block text-xs tracking-[0.2em] border-b border-black pb-1 hover:opacity-60">
          FOLLOW US
        </a>
      </section>
    </div>
  );
}
